import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { isEmpty, sum } from 'lodash';

import { AISuggestedEventModal, SleepBarAndInfo, MealSummary } from '../../../../../components';
import { useEventDetailsMenuContext } from '../../../../../data-access';
import type { UnifiedChatRichContext } from '../../../../../data-access';
import { hasSleepSummaryDataInRichContext } from '../helpers';
import { useAISuggestedHandler } from './useAISuggestedHandler';

import './AISuggestedEventSheetWrapper.scss';

interface AISuggestedEventSheetWrapperProps {
  disableInteraction?: boolean;
}

const AISuggestedEventSheetWrapper: React.FC<AISuggestedEventSheetWrapperProps> = ({
  disableInteraction = false,
}) => {
  const {
    aiSuggestedEvent,
    isAISuggestedEventModalVisible,
    handleAISuggestedEventModalClose,
    patchAISuggestedEvent,
  } = useEventDetailsMenuContext();

  const isNapEvent = aiSuggestedEvent?.autoScheduleFrom?.chatSessionId === 'nap';

  const {
    isSaving,
    isEditedManually,
    canUpdate,
    canAcceptCalendar,
    canReject,
    canDelete,
    isPredicted,
    sleepData,
    mealData,
    mealSummary,
    name,
    startTime,
    endTime,
    description,
    sessionType,
    handleAskAI,
    handleUpdate,
    handleAccept,
    handleReject,
    handleDelete,
    handleSaveEnergyFeedback,
    handleToggleTaskComplete,
    handleToggleRoutineComplete,
    handleSaveMealContent,
    isTaskStatusUpdating,
    routineCompleted,
    isRoutineStatusUpdating,
  } = useAISuggestedHandler({
    aiSuggestedEvent,
    handleAISuggestedEventModalClose,
    patchAISuggestedEvent,
  });

  const richContext = ((aiSuggestedEvent as any)?.richContext ?? null) as
    | UnifiedChatRichContext
    | null;
  const hasSleepRichContextData = useMemo(
    () => hasSleepSummaryDataInRichContext(richContext),
    [richContext]
  );
  const hasNoSleepData = isNapEvent
    ? false
    : sleepData
      ? sleepData.hasNoSleepData && !hasSleepRichContextData
      : !hasSleepRichContextData;

  const sleepActualDataComp = useMemo(() => {
    if (!sleepData || isEmpty(sleepData) || sleepData.hasNoSleepData) {
      return null;
    }
    const hasNoSleepStage = sum(Object.values(sleepData.sleepStage ?? {})) === 0;

    return (
      <div className="ai-suggested-event-sheet-wrapper__sleep-data-container">
        <SleepBarAndInfo
          targetSleepStage={sleepData.sleepStage}
          targetAwakeTime={sleepData.awakeTime}
          targetSleepTime={sleepData.sleepTime}
          hasNoSleepStage={hasNoSleepStage}
          heartRateSummary={sleepData.heartRateSummary}
          className="ai-suggested-event-sheet-wrapper__sleep-data-container__sleep-bar-and-info"
        />
        {sleepData.sleepSource && (
          <Typography variant="subtitle2" className="ai-suggested-event-sheet-wrapper__source-text">
            Data source: {sleepData.sleepSource}
          </Typography>
        )}
      </div>
    );
  }, [sleepData]);

  const mealActualDataComp = useMemo(
    () =>
      isEmpty(mealData) && isEmpty(mealSummary) ? null : (
        <MealSummary
          meal={mealData}
          mealSummary={mealSummary}
          style={{ borderRadius: 16, padding: 16 }}
        />
      ),
    [mealData, mealSummary]
  );

  // Create sleep data component
  const actualDataComp = useMemo(() => {
    if (sessionType === 'sleep') {
      return sleepActualDataComp;
    } else if (sessionType === 'meal') {
      return mealActualDataComp;
    } else {
      return null;
    }
  }, [sessionType, sleepActualDataComp, mealActualDataComp]);

  return (
    <>
      {aiSuggestedEvent && sessionType && (
        <AISuggestedEventModal
          visible={isAISuggestedEventModalVisible}
          sessionType={sessionType}
          name={name}
          startTime={startTime}
          endTime={endTime}
          description={description}
            forceEnableSaveCta={
              sessionType === 'workout' &&
              Boolean((aiSuggestedEvent as any)?.forceEnableSaveCta) &&
              (aiSuggestedEvent as any)?.forceEnableSaveCtaSource === 'contentCardCta'
            }
          isSaving={isSaving}
          isPredicted={isPredicted}
          isEditedManually={isEditedManually}
          actualDataComp={actualDataComp}
          considerAcrossDays={sessionType === 'sleep'}
          hasNoSleepData={hasNoSleepData}
          taskFrom={aiSuggestedEvent.taskFrom}
          routineFrom={aiSuggestedEvent.routineFrom}
          calendarId={aiSuggestedEvent.calendarId}
          energyFeedback={aiSuggestedEvent.eventMetrics?.energy}
          onAskAI={handleAskAI}
          onUpdate={handleUpdate}
          onClose={handleAISuggestedEventModalClose}
          onAcceptCalendar={handleAccept}
          onReject={handleReject}
          canUpdate={canUpdate}
          canAcceptCalendar={canAcceptCalendar}
          canReject={canReject}
          canDelete={canDelete}
          onDelete={handleDelete}
          onSaveEnergyFeedback={handleSaveEnergyFeedback}
          onToggleTaskComplete={handleToggleTaskComplete}
          onToggleRoutineComplete={handleToggleRoutineComplete}
          isTaskStatusUpdating={isTaskStatusUpdating}
          routineCompleted={routineCompleted}
          isRoutineUpdating={isRoutineStatusUpdating}
          disableInteraction={disableInteraction}
          richContext={richContext}
          onSaveMealContent={handleSaveMealContent}
        />
      )}
    </>
  );
};

export default AISuggestedEventSheetWrapper;
